import {
  all, put, select, spawn, takeEvery, takeLatest, throttle
} from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";

import { IProjectsResponse } from "Model/Responses/IProjectsResponse";
import { IMeta } from "Model/State/INotificationMeta";
import { IProject } from "Model/Storage/IProject";
import { hasElements, hasNoElements } from "Utils/Array";
import { isFalse, isTrue } from "Utils/Boolean";
import { getProjects, getProjectsById, updateProjects } from "Workers/Projects";
import { ProjectCookie } from "Utils/ProjectCookie";
import {
  projectsAdd,
  projectsClear,
  projectsExportDialog,
  uiAutoCompleteSuggestionsAdd,
  projectsFilterStart,
  projectsLastRequestIdListSet,
  projectsLastRequestOnSet,
  projectsRetrieveError,
  projectsRetrieveNext,
  projectsRetrieveStart,
  projectsRetrieveSuccess,
  projectsSetTotalCount,
  projectsUpdateError,
  projectsUpdateStart,
  projectsUpdateSuccess,
  projectsCopyLinkStart,
  projectsCopyLinkError,
  projectsCopyLinkSuccess,
  projectsDeleteSuccess,
  projectsCloneSuccess,
  uiSetSelectedProjectIds,
  projectsRetrieveExtendedSet
} from "../Actions";
import {
  selectHasProjects,
  selectProjectList,
  selectProjectsCount,
  selectSelectedOrActiveProjectIds,
  selectSelectedProjects
} from "../Selectors/Projects";
import { callWithAuth } from "./CallWithAuth";
import { projectsSortInitSaga } from "./Sort";
import { recalculateFiltersSaga } from "./Filter";

function* projectsRetrieveStartSaga(action: PayloadAction<ProjectCookie>) {
  const previousCookie = action?.payload ?? null;
  const isFirstCall = previousCookie == null;
  try {
    if (isFirstCall && (yield select(selectHasProjects))) {
      yield put(projectsClear());
    }

    const response: IProjectsResponse = yield callWithAuth(getProjects, previousCookie?.loadCookie ?? null);
    const currentCookie = new ProjectCookie(response.cookie);

    if (hasElements(response.projects)) {
      yield put(projectsAdd(response.projects));
    }

    if (isFirstCall) {
      yield all([
        // this will trigger the filter and sort saga for the first batch of projects
        put(projectsRetrieveSuccess()),
        put(projectsLastRequestOnSet(currentCookie.requestOn)),
        put(projectsLastRequestIdListSet(currentCookie.idList))
      ]);
    }

    const totalCount: number = yield select(selectProjectsCount);
    if (totalCount >= 2000 && totalCount <= 2500) {
      yield put(projectsRetrieveSuccess());
    }

    if (!response.hasMore) {
      // this will trigger the filter and sort saga for all remaining projects
      yield put(projectsRetrieveSuccess());
      return;
    }

    yield spawn(projectsRetrieveStartSaga, projectsRetrieveNext(currentCookie));
  } catch (e) {
    console.error(e);
    yield put(projectsRetrieveError(e));
  }
}

function* projectsUpdateStartSaga(action: PayloadAction<IProject[], "projects/update/start", IMeta>) {
  const { payload: projects, meta } = action;

  try {
    yield callWithAuth(updateProjects, projects);
    yield put(projectsUpdateSuccess(projects, meta.showNotification));

    if (isTrue(meta?.withExport, false)) {
      yield put(projectsExportDialog(true));
    }

    yield put(projectsFilterStart());
  } catch (e) {
    console.error(e);
    yield put(projectsUpdateError(e));
  }
}

function* projectLoadExtendedSaga() {
  const selectedProjects: IProject[] = yield select(selectSelectedProjects);
  if (hasNoElements(selectedProjects)) {
    return;
  }
  // only load max. 25 projects to prevent select all to trigger a massive load
  // or loading to many IDs and overflowing url length
  const selectedProjectIds = selectedProjects
    .slice(0, 24)
    .filter((project) => isFalse(project[`@isFullyLoaded`], false))
    .map((project) => project[`@id`]);

  if (hasNoElements(selectedProjectIds)) {
    return;
  }

  try {
    yield put(projectsRetrieveExtendedSet(true));
    const projectList: IProject[] = yield callWithAuth(getProjectsById, selectedProjectIds);
    if (hasNoElements(projectList)) {
      return;
    }

    yield put(projectsAdd(projectList.map((project) => ({ ...project, [`@isFullyLoaded`]: true }))));
    yield all([put(projectsRetrieveSuccess(false)), put(projectsRetrieveExtendedSet(false))]);
  } catch (e) {
    console.error(e);
    yield put(projectsRetrieveError(e));
    yield put(projectsRetrieveExtendedSet(false));
  }
}

export function* projectsCountSaga() {
  const totalCount: number = yield select(selectProjectsCount);
  yield put(projectsSetTotalCount(totalCount));
}

function* projectsCopyLinkStartSaga() {
  const selectedProjectIds = yield select(selectSelectedOrActiveProjectIds);
  if (selectedProjectIds?.length !== 1) {
    return;
  }

  const [selectedProjectId] = selectedProjectIds;
  try {
    navigator.clipboard.writeText(`${document.location.origin}?pid=${selectedProjectId}`);
    yield put(projectsCopyLinkSuccess());
  } catch (error) {
    console.error(error);
    yield put(projectsCopyLinkError());
  }
}

function* projectsRetrieveSuccessSaga() {
  const projects: IProject[] = yield select(selectProjectList);
  yield all([put(uiAutoCompleteSuggestionsAdd(projects)), spawn(sortAndFilterSaga)]);
}

export function* sortAndFilterSaga({ filter = true, sort = true } = {}) {
  if (filter) {
    yield recalculateFiltersSaga();
  }

  if (sort) {
    yield projectsSortInitSaga();
  }
}

export function* StorageSaga(): Generator<unknown, void> {
  yield all([
    takeEvery(projectsRetrieveStart, projectsRetrieveStartSaga),
    takeEvery(projectsRetrieveSuccess, projectsRetrieveSuccessSaga),
    takeEvery(projectsUpdateStart, projectsUpdateStartSaga),
    takeEvery(projectsCopyLinkStart, projectsCopyLinkStartSaga),
    throttle(500, [projectsAdd, projectsDeleteSuccess, projectsCloneSuccess], projectsCountSaga),
    takeLatest(uiSetSelectedProjectIds, projectLoadExtendedSaga)
  ]);
}
